import { Vector3 } from 'three';

import { Bone, Skeleton } from '../../skeleton/core';
import {
  ROLE_TERMS,
  isFootLikeName,
  isHockLikeName,
  isStructuralLegAuxiliaryName,
  isSideHipConnectorName,
  isTerminalHelperLikeName,
  isToeLikeName,
  isUpperLegChainCandidateName,
  isLegHipChainName,
} from '../../schema/mapping';
import {
  jointPrebendRatio,
  lateralSpreadScore,
  symmetryScore as computeSymmetryScoreFn,
  ScoreBreakdown,
  totalScore,
  WEIGHTS,
  toeParentScore,
  toeDirectionAlignmentScore,
} from '../scoring';
import type { SkeletonSpatialData } from '../spatial';
import type { RecognizerConfig } from '../config';
import {
  pruneInlineTwistBones,
  chainSideHint,
  assignSide,
  _isSubdivisionBone,
} from '../helpers';
import { RoleCandidate } from '../config';

// ─── Position helpers ────────────────────────────────────────────────────

function boneWorldPos(bone: Bone): Vector3 {
  const e = bone.restWorld.elements;
  return new Vector3(e[12], e[13], e[14]);
}

function chainDirection(skel: Skeleton, chain: number[]): Vector3 {
  if (chain.length === 0) return new Vector3();
  const sum = new Vector3();
  let count = 0;
  for (const idx of chain) {
    const b = skel.bone(idx);
    if (b.length > 0) {
      sum.add(b.direction);
      count++;
    }
  }
  if (count === 0) return new Vector3();
  sum.divideScalar(count);
  const norm = sum.length();
  if (norm < 1e-8) return new Vector3();
  return sum.divideScalar(norm);
}

// ─── Constants ───────────────────────────────────────────────────────────

const PERFECT_INLINE_ANGLE = 1.0;

const JOINT_KEYWORDS = new Set([
  'elbow', 'forearm', 'lowerarm', 'lower_arm', 'wrist', 'hand', 'arm',
  'knee', 'calf', 'shin', 'lowerleg', 'lower_leg', 'ankle', 'foot', 'leg',
  'shoulder', 'hip', 'thigh', 'upper', 'head', 'neck', 'spine',
  'ひじ', 'ひざ', '手首', '足首',
]);

const MIN_BEND_ANGLE = 25.0;

// ─── Chain traversal helpers ─────────────────────────────────────────────

function extractPathsToLeaves(
  skel: Skeleton,
  startIdx: number,
  ignoreAux = true,
): number[][] {
  const paths: number[][] = [];

  function getEffectiveChildren(idx: number): number[] {
    const bone = skel.bone(idx);
    const result: number[] = [];
    for (const c of bone.children) {
      const child = skel.bone(c);
      if (
        ignoreAux &&
        child.auxiliary &&
        !isStructuralLegAuxiliaryName(child.name)
      ) {
        result.push(...getEffectiveChildren(c));
      } else {
        result.push(c);
      }
    }
    return result;
  }

  function dfs(idx: number, current: number[]): void {
    const bone = skel.bone(idx);
    if (
      ignoreAux &&
      bone.auxiliary &&
      !isStructuralLegAuxiliaryName(bone.name)
    ) {
      return;
    }
    current.push(idx);
    const children = getEffectiveChildren(idx);
    if (children.length === 0) {
      paths.push([...current]);
    } else {
      for (const child of children) {
        dfs(child, current);
      }
    }
    current.pop();
  }

  dfs(startIdx, []);
  return paths;
}

function chainLength(skel: Skeleton, chain: number[] | readonly number[]): number {
  return chain.reduce((sum, idx) => sum + skel.bone(idx).length, 0);
}

export function trimLeadingLegConnector(skel: Skeleton, chain: number[]): number[] {
  if (chain.length < 2) return chain;
  const firstName = skel.bone(chain[0]).name.toLowerCase();
  const secondName = skel.bone(chain[1]).name.toLowerCase();
  return isSideHipConnectorName(firstName) && isLegHipChainName(secondName)
    ? chain.slice(1)
    : chain;
}

function bestTwoSymmetricChains(
  candidateChains: number[][],
  skel: Skeleton,
  axisVec: Vector3,
  referencePos?: Vector3,
  minSpread = 0.0,
): [number[] | null, number[] | null, number] {
  let bestScore = -1.0;
  let bestPair: [number[] | null, number[] | null] = [null, null];

  const axisLen = axisVec.length();
  const axis = axisLen > 1e-8 ? axisVec.clone().divideScalar(axisLen) : axisVec.clone();

  for (let i = 0; i < candidateChains.length; i++) {
    const chainA = candidateChains[i];
    for (let j = i + 1; j < candidateChains.length; j++) {
      const chainB = candidateChains[j];
      const lenA = chainLength(skel, chainA);
      const lenB = chainLength(skel, chainB);
      const dirA = chainDirection(skel, chainA);
      const dirB = chainDirection(skel, chainB);
      const mirrorDot = dirA.dot(dirB.clone().negate());
      const depthA = skel.bone(chainA[0]).depth;
      const depthB = skel.bone(chainB[0]).depth;
      let score = computeSymmetryScoreFn(lenA, lenB, depthA, depthB, mirrorDot);

      if (referencePos !== undefined && minSpread > 0) {
        const posA = boneWorldPos(skel.bone(chainA[0]));
        const posB = boneWorldPos(skel.bone(chainB[0]));
        const spread = lateralSpreadScore(posA, posB, axis, referencePos, minSpread);
        score = 0.8 * score + 0.2 * spread;
      }

      if (score > bestScore) {
        bestScore = score;
        bestPair = [[...chainA], [...chainB]];
      }
    }
  }

  return [bestPair[0], bestPair[1], bestScore];
}

// ─── Spatial leg detection ───────────────────────────────────────────────

function findSpatialLegRoots(
  skel: Skeleton,
  pelvisIdx: number,
  spatialData: SkeletonSpatialData,
  lateralAxis: Vector3,
  pelvisPos: Vector3,
): [number[], number[]] {
  const leftRoots: number[] = [];
  const rightRoots: number[] = [];

  if (spatialData == null) return [leftRoots, rightRoots];

  const bp = spatialData.getBranchingPoint(pelvisIdx);
  if (bp == null) return [leftRoots, rightRoots];

  if (!bp.hasSymmetricChildren || !bp.hasDownwardChildren) {
    return [leftRoots, rightRoots];
  }

  for (const [idxA, idxB] of bp.symmetricPairs) {
    const boneA = skel.bone(idxA);
    const boneB = skel.bone(idxB);
    const posA = boneWorldPos(boneA);
    const posB = boneWorldPos(boneB);

    const lateralA = posA.dot(lateralAxis);
    const lateralB = posB.dot(lateralAxis);

    if (lateralA < lateralB) {
      leftRoots.push(idxA);
      rightRoots.push(idxB);
    } else {
      leftRoots.push(idxB);
      rightRoots.push(idxA);
    }
  }

  return [leftRoots, rightRoots];
}

// ─── Chain metrics ───────────────────────────────────────────────────────

interface ChainMetrics {
  path: number[];
  chainVector: Vector3;
  chainLength: number;
  downAlignment: number;
  lateralOffset: number;
  endpointHeightDelta: number;
  kneePrebendRatio: number;
  legKeywordCount: number;
  footKeywordFound: boolean;
  spineKeywordFound: boolean;
  endpointIsDecorative: boolean;
  boneCount: number;
}

function computeChainMetrics(
  skel: Skeleton,
  path: number[],
  pelvisPos: Vector3,
  downAxis: Vector3,
  lateralAxis: Vector3,
): ChainMetrics {
  const startPos = boneWorldPos(skel.bone(path[0]));
  const endPos = boneWorldPos(skel.bone(path[path.length - 1]));
  const chainVec = endPos.clone().sub(startPos);

  const chainNorm = chainVec.length();
  const chainDir = chainNorm > 1e-8
    ? chainVec.clone().divideScalar(chainNorm)
    : new Vector3();

  const downAlignment = chainDir.dot(downAxis);
  const totalLength = path.reduce((sum, idx) => sum + skel.bone(idx).length, 0);
  const lateralOff = startPos.clone().sub(pelvisPos).dot(lateralAxis);
  const endpointHeight = endPos.clone().sub(pelvisPos).dot(downAxis);

  let kneePrebendRatio = 0.0;
  if (path.length >= 3) {
    kneePrebendRatio = jointPrebendRatio(
      startPos,
      boneWorldPos(skel.bone(path[1])),
      boneWorldPos(skel.bone(path[2])),
      lateralAxis,
      downAxis,
    );
  }

  const legKeywords = Array.from(new Set([
    ...(ROLE_TERMS['UpperLeg'] ?? []),
    ...(ROLE_TERMS['LowerLeg'] ?? []),
    ...(ROLE_TERMS['Foot'] ?? []),
    ...(ROLE_TERMS['Toe'] ?? []),
    'leg', 'hip',
  ]));
  const footKeywords = Array.from(new Set([
    ...(ROLE_TERMS['Foot'] ?? []),
    ...(ROLE_TERMS['Toe'] ?? []),
  ]));
  const spineKeywords = Array.from(new Set([
    ...(ROLE_TERMS['Spine'] ?? []),
    ...(ROLE_TERMS['Chest'] ?? []),
  ]));
  const decorativeKeywords = [
    'crystal', 'fx', 'effect', 'object', 'dummy', 'nub',
    'null', 'helper', 'point', 'marker',
  ];

  let legKeywordCount = 0;
  let footKeywordFound = false;
  let spineKeywordFound = false;

  for (const idx of path) {
    const name = skel.bone(idx).name.toLowerCase();
    for (const kw of legKeywords) {
      if (name.includes(kw)) { legKeywordCount++; break; }
    }
    for (const kw of footKeywords) {
      if (name.includes(kw)) { footKeywordFound = true; break; }
    }
    for (const kw of spineKeywords) {
      if (name.includes(kw)) { spineKeywordFound = true; break; }
    }
  }

  const endpointName = skel.bone(path[path.length - 1]).name.toLowerCase();
  const endpointIsDecorative = decorativeKeywords.some(kw => endpointName.includes(kw));

  return {
    path,
    chainVector: chainVec,
    chainLength: totalLength,
    downAlignment,
    lateralOffset: lateralOff,
    endpointHeightDelta: endpointHeight,
    kneePrebendRatio,
    legKeywordCount,
    footKeywordFound,
    spineKeywordFound,
    endpointIsDecorative,
    boneCount: path.length,
  };
}

// ─── Leg candidate selection ─────────────────────────────────────────────

function isLegCandidate(
  metrics: ChainMetrics,
  minBones = 3,
  minLateralOffset = 0.0,
): boolean {
  if (metrics.boneCount < minBones) return false;
  if (metrics.spineKeywordFound) return false;

  const pointsDown = metrics.downAlignment > 0.3;
  const hasLegNaming = metrics.legKeywordCount > 0;
  if (!pointsDown && !hasLegNaming) return false;

  const endpointBelow = metrics.endpointHeightDelta > 5.0;
  const hasFootNaming = metrics.footKeywordFound;
  if (!endpointBelow && !hasFootNaming) return false;

  if (minLateralOffset > 0 && Math.abs(metrics.lateralOffset) < minLateralOffset) {
    return false;
  }

  return true;
}

function scoreLegCandidate(
  metrics: ChainMetrics,
  spatialLegRoots?: Set<number>,
): number {
  let score = 0.0;

  score += metrics.legKeywordCount * 1.0;
  if (metrics.footKeywordFound) score += 2.0;

  if (metrics.downAlignment > 0.3) score += metrics.downAlignment;

  if (metrics.endpointHeightDelta > 5.0) {
    score += Math.min(metrics.endpointHeightDelta / 30.0, 1.0);
  }

  if (metrics.endpointIsDecorative) score -= 10.0;

  if (metrics.kneePrebendRatio > 0.005) {
    score += 0.5;
  } else if (metrics.kneePrebendRatio < -0.005) {
    score -= 1.5;
  }

  if (spatialLegRoots && metrics.path.length > 0) {
    if (spatialLegRoots.has(metrics.path[0])) score += 3.0;
  }

  return score;
}

// ─── Path trimming helpers ───────────────────────────────────────────────

function trimPathAtToeFanRoot(skel: Skeleton, path: number[]): number[] {
  let toeLikeCount = 0;
  for (let i = 0; i < path.length; i++) {
    const bone = skel.bone(path[i]);
    const name = bone.name.toLowerCase();
    if (!isToeLikeName(name)) continue;
    toeLikeCount += 1;

    const branchingToeChildren = bone.children.filter((childIdx) => {
      const child = skel.bone(childIdx);
      if (child.auxiliary) return false;
      const childName = child.name.toLowerCase();
      return isToeLikeName(childName) || isTerminalHelperLikeName(childName);
    });
    if (toeLikeCount >= 2 && branchingToeChildren.length >= 2) {
      return path.slice(0, i + 1);
    }
  }

  return path;
}

export function trimPathAtFoot(skel: Skeleton, path: number[]): number[] {
  let trimmedLength = path.length;
  while (trimmedLength > 0) {
    const name = skel.bone(path[trimmedLength - 1]).name.toLowerCase();
    if (!isTerminalHelperLikeName(name)) break;
    trimmedLength -= 1;
  }

  const meaningfulPath =
    trimmedLength === path.length ? path : path.slice(0, trimmedLength);
  if (meaningfulPath.length === 0) return meaningfulPath;
  const normalizedPath = trimPathAtToeFanRoot(skel, meaningfulPath);

  let footIdx: number | null = null;
  for (let i = 0; i < normalizedPath.length; i++) {
    const name = skel.bone(normalizedPath[i]).name.toLowerCase();
    if (isFootLikeName(name)) {
      footIdx = i;
      break;
    }
  }

  if (footIdx === null) return normalizedPath;

  const distalToeLikeCount = normalizedPath
    .slice(footIdx + 1)
    .map((idx) => skel.bone(idx).name.toLowerCase())
    .filter((name) => !isTerminalHelperLikeName(name))
    .filter((name) => isToeLikeName(name)).length;

  // Keep authored foot-to-toe continuations intact so downstream toe
  // splitting and digitigrade typing can reason about the distal segment
  // instead of collapsing everything at the first foot-like joint.
  if (distalToeLikeCount >= 1) return normalizedPath;

  return normalizedPath.slice(0, footIdx + 1);
}

function deduplicatePaths(paths: number[][]): number[][] {
  const seen = new Set<string>();
  const unique: number[][] = [];
  for (const path of paths) {
    const key = path.join(',');
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(path);
    }
  }
  return unique;
}

// ─── Container handling helpers ──────────────────────────────────────────

function isLegConnectorBone(
  skel: Skeleton,
  bone: Bone,
  pelvisPos: Vector3,
): boolean {
  const nameLower = bone.name.toLowerCase();
  if (!isSideHipConnectorName(nameLower)) return false;
  if (!bone.hasSide) return false;

  const latAxis = new Vector3(1, 0, 0);

  const allPositions = skel.bones.map(b => boneWorldPos(b));
  const lateralProjections = allPositions.map(p => p.dot(latAxis));
  const skelLateralExtent = lateralProjections.length > 0
    ? Math.max(...lateralProjections) - Math.min(...lateralProjections)
    : 1.0;
  const offsetThreshold = Math.max(0.03 * skelLateralExtent, 1e-4);

  for (const childIdx of bone.children) {
    const child = skel.bone(childIdx);
    if (child.auxiliary) continue;

    const childPos = boneWorldPos(child);
    const lateralOffset = Math.abs(childPos.clone().sub(pelvisPos).dot(latAxis));
    if (lateralOffset > offsetThreshold) return true;

    const childNameLower = child.name.toLowerCase();
    if (isUpperLegChainCandidateName(childNameLower)) return true;
  }

  return false;
}

function extractLegPathsFromContainer(
  skel: Skeleton,
  containerBone: Bone,
  allPaths: number[][],
  pelvisPos: Vector3,
): void {
  for (const childIdx of containerBone.children) {
    const childBone = skel.bone(childIdx);
    if (childBone.auxiliary) continue;

    if (isLegConnectorBone(skel, childBone, pelvisPos)) {
      for (const gcIdx of childBone.children) {
        const gcBone = skel.bone(gcIdx);
        if (gcBone.auxiliary) continue;
        const paths = extractPathsToLeaves(skel, gcIdx, true);
        allPaths.push(...paths);
      }
    } else {
      const paths = extractPathsToLeaves(skel, childIdx, true);
      allPaths.push(...paths);
    }
  }
}

// ─── Lateral extent helper ───────────────────────────────────────────────

function computeLateralExtent(skel: Skeleton, lateralAxis: Vector3): number {
  if (skel.bones.length === 0) return 0;
  const projections = skel.bones.map(b => boneWorldPos(b).dot(lateralAxis));
  return Math.max(...projections) - Math.min(...projections);
}

// ═════════════════════════════════════════════════════════════════════════
// MAIN DETECTION
// ═════════════════════════════════════════════════════════════════════════

export function detectLegs(
  skel: Skeleton,
  pelvis: Bone,
  lateralAxis: Vector3,
  downAxis: Vector3,
  config: RecognizerConfig,
  spatialData?: SkeletonSpatialData | null,
): [number[] | null, number[] | null, number] {
  const pelvisPos = boneWorldPos(pelvis);
  const lateralExtent = computeLateralExtent(skel, lateralAxis);

  const spatialLegRoots = new Set<number>();
  if (spatialData != null) {
    const [spatialLeftRoots, spatialRightRoots] = findSpatialLegRoots(
      skel, pelvis.index, spatialData, lateralAxis, pelvisPos,
    );
    for (const idx of spatialLeftRoots) spatialLegRoots.add(idx);
    for (const idx of spatialRightRoots) spatialLegRoots.add(idx);
  }

  // PHASE 1: COLLECTION
  const allPaths: number[][] = [];

  for (const childIdx of pelvis.children) {
    const childBone = skel.bone(childIdx);

    if (childBone.auxiliary) {
      const nameLower = childBone.name.toLowerCase();
      const upperLegTerms = [...(ROLE_TERMS['UpperLeg'] ?? []), 'leg', 'hip'];
      const isLegConn = upperLegTerms.some(tok => nameLower.includes(tok));
      if (!isLegConn) continue;
    }

    const torsoTokens = Array.from(new Set([
      ...(ROLE_TERMS['Spine'] ?? []),
      ...(ROLE_TERMS['Chest'] ?? []),
      ...(ROLE_TERMS['Neck'] ?? []),
      ...(ROLE_TERMS['Head'] ?? []),
      'upperbody',
    ]));
    const nameLowerFull = childBone.name.toLowerCase();
    if (torsoTokens.some(tok => nameLowerFull.includes(tok))) {
      let hasLegNamedChild = false;
      const upperTerms = [...(ROLE_TERMS['UpperLeg'] ?? []), 'leg', 'hip'];
      for (const gcIdx of childBone.children) {
        const gcName = skel.bone(gcIdx).name.toLowerCase();
        if (upperTerms.some(p => gcName.includes(p))) {
          hasLegNamedChild = true;
          break;
        }
      }
      if (!hasLegNamedChild) continue;
    }

    const nameLower = childBone.name.toLowerCase();
    const containerPatterns = ['pelvis', 'lower_body', 'lowerbody', 'hips'];
    let isContainer = containerPatterns.some(tok => nameLower.includes(tok));

    if (!isContainer) {
      const childPos = boneWorldPos(childBone);
      const childLateral = Math.abs(childPos.clone().sub(pelvisPos).dot(lateralAxis));
      const centerThreshold = Math.max(0.05 * lateralExtent, 1e-4);

      if (childLateral < centerThreshold) {
        const offsetThreshold = Math.max(0.03 * lateralExtent, 1e-4);
        let hasOffsetDescendants = false;

        for (const gcIdx of childBone.children) {
          const gcBone = skel.bone(gcIdx);
          if (gcBone.auxiliary) continue;
          const gcPos = boneWorldPos(gcBone);
          const gcLateral = Math.abs(gcPos.clone().sub(pelvisPos).dot(lateralAxis));
          if (gcLateral > offsetThreshold) {
            hasOffsetDescendants = true;
            break;
          }
          for (const ggcIdx of gcBone.children) {
            const ggcBone = skel.bone(ggcIdx);
            if (ggcBone.auxiliary) continue;
            const ggcPos = boneWorldPos(ggcBone);
            const ggcLateral = Math.abs(ggcPos.clone().sub(pelvisPos).dot(lateralAxis));
            if (ggcLateral > offsetThreshold) {
              hasOffsetDescendants = true;
              break;
            }
          }
          if (hasOffsetDescendants) break;
        }

        if (hasOffsetDescendants) isContainer = true;
      }
    }

    if (isContainer && spatialLegRoots.has(childIdx)) {
      isContainer = false;
    }

    if (childBone.hasSide) isContainer = false;

    const connPatterns = ['hip', 'crotch'];
    if (!isContainer && connPatterns.some(p => nameLower.includes(p)) && childBone.hasSide) {
      const thighTokens = ROLE_TERMS['UpperLeg'] ?? [];
      for (const gcIdx of childBone.children) {
        const gcBone = skel.bone(gcIdx);
        if (gcBone.auxiliary) continue;
        const gcNameLower = gcBone.name.toLowerCase();
        if (thighTokens.some(tok => gcNameLower.includes(tok))) {
          isContainer = true;
          break;
        }
      }
    }

    if (isContainer) {
      extractLegPathsFromContainer(skel, childBone, allPaths, pelvisPos);
    } else {
      const paths = extractPathsToLeaves(skel, childIdx, true);
      allPaths.push(...paths);
    }
  }

  // PHASE 1.5: GENERATE TRIMMED PATH VARIANTS
  const trimmedPaths = allPaths.map(path => trimPathAtFoot(skel, path));
  let allPathVariants = [...allPaths, ...trimmedPaths];
  allPathVariants = deduplicatePaths(allPathVariants);

  // PHASE 2: CHAIN METRICS
  const allMetrics: ChainMetrics[] = [];
  for (const path of allPathVariants) {
    if (path.length < 3) continue;
    allMetrics.push(computeChainMetrics(skel, path, pelvisPos, downAxis, lateralAxis));
  }

  // PHASE 2.5: CALCULATE LATERAL OFFSET THRESHOLD
  const minLateralOffset = lateralExtent > 0 ? 0.03 * lateralExtent : 0.0;

  // PHASE 3: LEG CANDIDATE SELECTION
  let candidates: ChainMetrics[] = [];
  for (const metrics of allMetrics) {
    if (isLegCandidate(metrics, 3, minLateralOffset)) {
      candidates.push(metrics);
    }
  }

  if (candidates.length < 2) {
    const scored = allMetrics
      .map(m => [scoreLegCandidate(m, spatialLegRoots), m] as [number, ChainMetrics])
      .sort((a, b) => b[0] - a[0]);
    candidates = scored.slice(0, 4).map(([, m]) => m);
  }

  if (candidates.length < 2) return [null, null, 0.0];

  // PHASE 3.5: PREFER NON-DECORATIVE CANDIDATES
  const nonDecorative = candidates.filter(m => !m.endpointIsDecorative);
  if (nonDecorative.length >= 2) candidates = nonDecorative;

  candidates.sort(
    (a, b) => scoreLegCandidate(b, spatialLegRoots) - scoreLegCandidate(a, spatialLegRoots),
  );
  const candidatePaths = candidates.map(m => m.path);

  // PHASE 4: SYMMETRIC PAIRING
  const minSpread = lateralExtent > 0 ? Math.max(0.05 * lateralExtent, 1.0) : 1.0;
  let leftChain: number[] | null = null;
  let rightChain: number[] | null = null;
  let symmetry = 0.0;

  for (const nCandidates of [2, 4, 6, candidatePaths.length]) {
    if (nCandidates > candidatePaths.length) break;
    const subset = candidatePaths.slice(0, nCandidates);
    const [l, r, sym] = bestTwoSymmetricChains(subset, skel, lateralAxis, pelvisPos, minSpread);

    if (l && r && sym > 0.3) {
      const lHint = chainSideHint(skel, l);
      const rHint = chainSideHint(skel, r);
      if (lHint && rHint && lHint === rHint) continue;
      leftChain = l;
      rightChain = r;
      symmetry = sym;
      break;
    }
  }

  if (!leftChain || !rightChain) {
    [leftChain, rightChain, symmetry] = bestTwoSymmetricChains(
      candidatePaths, skel, lateralAxis, pelvisPos, minSpread,
    );
  }

  if (!leftChain || !rightChain) return [null, null, 0.0];

  // PHASE 5: SIDE ASSIGNMENT
  let leftHint = chainSideHint(skel, leftChain);
  let rightHint = chainSideHint(skel, rightChain);

  if (
    (leftHint === 'Right' && rightHint !== 'Right') ||
    (rightHint === 'Left' && leftHint !== 'Left')
  ) {
    [leftChain, rightChain] = [rightChain, leftChain];
  } else if (leftHint == null && rightHint == null) {
    let spatialSideA: string | null = null;
    let spatialSideB: string | null = null;
    if (spatialData) {
      spatialSideA = spatialData.getSideFromSpatial(leftChain[0], skel);
      spatialSideB = spatialData.getSideFromSpatial(rightChain[0], skel);
    }

    if (spatialSideA !== spatialSideB && spatialSideA != null) {
      if (spatialSideA === 'Right') {
        [leftChain, rightChain] = [rightChain, leftChain];
      }
    } else {
      const leftX = skel.bone(leftChain[0]).restWorld.elements[12];
      const rightX = skel.bone(rightChain[0]).restWorld.elements[12];
      if (leftX < rightX) {
        [leftChain, rightChain] = [rightChain, leftChain];
      }
    }
  }

  const torsoPrefixes = ['spine', 'neck', 'head', 'chest', 'upperbody'];
  function trimTorsoPrefix(chain: number[]): number[] {
    let result = chain;
    while (result.length > 1 && torsoPrefixes.some(tok => skel.bone(result[0]).name.toLowerCase().includes(tok))) {
      result = result.slice(1);
    }
    return result;
  }

  leftChain = trimTorsoPrefix(leftChain);
  rightChain = trimTorsoPrefix(rightChain);

  return [leftChain, rightChain, symmetry];
}

// ═════════════════════════════════════════════════════════════════════════
// HIND LEG (DIGITIGRADE) CLASSIFICATION
// ═════════════════════════════════════════════════════════════════════════

function countSignificantBends(
  skel: Skeleton,
  chain: number[],
  minAngle = MIN_BEND_ANGLE,
): [number, number][] {
  const bends: [number, number][] = [];
  if (chain.length < 3) return bends;

  for (let i = 1; i < chain.length - 1; i++) {
    const prevPos = boneWorldPos(skel.bone(chain[i - 1]));
    const currPos = boneWorldPos(skel.bone(chain[i]));
    const nextPos = boneWorldPos(skel.bone(chain[i + 1]));

    const v1 = currPos.clone().sub(prevPos);
    const v2 = nextPos.clone().sub(currPos);
    const n1 = v1.length();
    const n2 = v2.length();
    if (n1 < 1e-6 || n2 < 1e-6) continue;

    v1.divideScalar(n1);
    v2.divideScalar(n2);
    const dot = Math.max(-1.0, Math.min(1.0, v1.dot(v2)));
    const angle = Math.acos(dot) * (180 / Math.PI);

    if (angle >= minAngle) bends.push([i, angle]);
  }

  return bends;
}

function hasHockNaming(skel: Skeleton, chain: number[]): number | null {
  for (let i = 0; i < chain.length; i++) {
    const nameLower = skel.bone(chain[i]).name.toLowerCase();
    if (isHockLikeName(nameLower)) return i;
  }
  return null;
}

export function classifyLegType(
  skel: Skeleton,
  chain: number[],
  downAxis: Vector3,
  lateralAxis?: Vector3 | null,
): string {
  const meaningfulChain = trimPathAtFoot(skel, chain);
  const hockPos = hasHockNaming(skel, meaningfulChain);
  if (hockPos !== null) return 'digitigrade';

  if (meaningfulChain.length < 5) return 'plantigrade';

  const hockCandidatePos = meaningfulChain.length - 3;
  const hockCandidateName = skel
    .bone(meaningfulChain[hockCandidatePos])
    .name
    .toLowerCase();
  if (isFootLikeName(hockCandidateName) && !isHockLikeName(hockCandidateName)) {
    const distalToeLikeCount = meaningfulChain
      .slice(hockCandidatePos + 1)
      .map((idx) => skel.bone(idx).name.toLowerCase())
      .filter((name) => !isTerminalHelperLikeName(name))
      .filter((name) => isToeLikeName(name)).length;
    if (distalToeLikeCount < 2) {
      return 'plantigrade';
    }
  }

  const bends = countSignificantBends(skel, meaningfulChain);
  if (bends.length < 2) return 'plantigrade';

  if (meaningfulChain.length >= 5) {
    const hockCandidateIdx = meaningfulChain[meaningfulChain.length - 3];
    const footCandidateIdx = meaningfulChain[meaningfulChain.length - 2];

    const hockWorld = boneWorldPos(skel.bone(hockCandidateIdx));
    const footWorld = boneWorldPos(skel.bone(footCandidateIdx));

    const hockToFoot = footWorld.clone().sub(hockWorld);
    const segLen = hockToFoot.length();

    let totalChainLen = 0;
    for (let i = 0; i < meaningfulChain.length - 1; i++) {
      const posA = boneWorldPos(skel.bone(meaningfulChain[i]));
      const posB = boneWorldPos(skel.bone(meaningfulChain[i + 1]));
      totalChainLen += posA.distanceTo(posB);
    }

    if (segLen > 1e-6 && totalChainLen > 1e-6) {
      const segDir = hockToFoot.clone().divideScalar(segLen);
      const downComponent = segDir.dot(downAxis);
      const lengthRatio = segLen / totalChainLen;

      const passesDirection = downComponent > 0.5;
      const passesLength = lengthRatio >= 0.15;

      const startPos = boneWorldPos(skel.bone(meaningfulChain[0]));
      const endPos = boneWorldPos(
        skel.bone(meaningfulChain[meaningfulChain.length - 1]),
      );
      const totalVerticalDrop = Math.abs(endPos.clone().sub(startPos).dot(downAxis));

      const hockToEnd = boneWorldPos(
        skel.bone(meaningfulChain[meaningfulChain.length - 1]),
      ).clone().sub(hockWorld);
      const hockVerticalDrop = Math.abs(hockToEnd.dot(downAxis));

      const verticalDropRatio = totalVerticalDrop > 1e-6
        ? hockVerticalDrop / totalVerticalDrop
        : 0.0;
      const passesVerticalDrop = verticalDropRatio >= 0.25;

      let passesHockBend = true;
      if (lateralAxis != null && meaningfulChain.length >= 4) {
        const upAxis = downAxis.clone().negate();
        const forwardAxis = new Vector3().crossVectors(lateralAxis, upAxis);
        const fn = forwardAxis.length();
        if (fn > 1e-8) {
          forwardAxis.divideScalar(fn);

          const kneePos = boneWorldPos(skel.bone(meaningfulChain[1]));
          const hipToToe = endPos.clone().sub(startPos);
          const hipToeLen = hipToToe.length();
          let kneeFwd = 0.0;
          if (hipToeLen > 1e-6) {
            const lineDir = hipToToe.clone().divideScalar(hipToeLen);
            const kv = kneePos.clone().sub(startPos);
            const kneeOnLine = startPos.clone().addScaledVector(lineDir, kv.dot(lineDir));
            kneeFwd = kneePos.clone().sub(kneeOnLine).dot(forwardAxis);
          }

          const kneeToToe = endPos.clone().sub(kneePos);
          const ktLen = kneeToToe.length();
          let hockFwd = 0.0;
          if (ktLen > 1e-6) {
            const ktDir = kneeToToe.clone().divideScalar(ktLen);
            const hv = hockWorld.clone().sub(kneePos);
            const hockOnLine = kneePos.clone().addScaledVector(ktDir, hv.dot(ktDir));
            hockFwd = hockWorld.clone().sub(hockOnLine).dot(forwardAxis);
          }

          const kneeThreshold = hipToeLen * 0.01;
          const hockThreshold = ktLen * 0.01;
          if (Math.abs(kneeFwd) > kneeThreshold && Math.abs(hockFwd) > hockThreshold) {
            if (kneeFwd * hockFwd > 0) passesHockBend = false;
          }
        }
      }

      if (passesDirection && passesLength && passesVerticalDrop && passesHockBend) {
        return 'digitigrade';
      }
    }
  }

  return 'plantigrade';
}

export function shouldMirrorDigitigradeLegType(
  skel: Skeleton,
  digitigradeChain: number[],
  candidateChain: number[],
  symmetry: number,
): boolean {
  if (symmetry < 0.75) return false;
  if (digitigradeChain.length !== candidateChain.length) return false;
  if (digitigradeChain.length < 5) return false;

  const digitigradeBends = countSignificantBends(skel, digitigradeChain);
  const candidateBends = countSignificantBends(skel, candidateChain);
  if (digitigradeBends.length < 2 || candidateBends.length < 2) return false;
  if (Math.abs(digitigradeBends.length - candidateBends.length) > 1) return false;

  for (let i = 0; i < digitigradeChain.length; i++) {
    const leftLen = skel.bone(digitigradeChain[i]).length;
    const rightLen = skel.bone(candidateChain[i]).length;
    const maxLen = Math.max(leftLen, rightLen);
    if (maxLen < 1e-6) continue;
    const similarity = Math.min(leftLen, rightLen) / maxLen;
    if (similarity < 0.75) return false;
  }

  const digitigradeEnd = skel.bone(digitigradeChain[digitigradeChain.length - 1]);
  const candidateEnd = skel.bone(candidateChain[candidateChain.length - 1]);
  if (digitigradeEnd.children.length > 0 || candidateEnd.children.length > 0) return false;

  return true;
}

// ═════════════════════════════════════════════════════════════════════════
// TOE DETECTION
// ═════════════════════════════════════════════════════════════════════════

function findToeCandidates(
  skel: Skeleton,
  legChain: number[],
  role: string,
): RoleCandidate[] {
  const candidates: RoleCandidate[] = [];
  if (legChain.length < 3) return candidates;

  let footIdx: number | null = null;
  let footChainPos: number | null = null;
  for (let i = 2; i < legChain.length; i++) {
    const name = skel.bone(legChain[i]).name.toLowerCase();
    if (['ankle', 'foot', 'pie', 'pied'].some((tok) => name.includes(tok))) {
      footIdx = legChain[i];
      footChainPos = i;
      break;
    }
  }

  if (footIdx == null) {
    footIdx = legChain[2];
    footChainPos = 2;
  }

  const footBone = skel.bone(footIdx);
  const footDir = footBone.direction;
  const legDir = chainDirection(skel, legChain.slice(0, footChainPos! + 1));

  const potentialToes: number[] = [];
  for (let i = footChainPos! + 1; i < legChain.length; i++) {
    potentialToes.push(legChain[i]);
  }
  for (const childIdx of footBone.children) {
    if (!potentialToes.includes(childIdx) && !skel.bone(childIdx).auxiliary) {
      potentialToes.push(childIdx);
    }
  }

  for (const boneIdx of potentialToes) {
    const bone = skel.bone(boneIdx);
    const nameLower = bone.name.toLowerCase();

    const isFootNumbered =
      ['foot', 'pie', 'pied'].some((tok) => nameLower.includes(tok)) &&
      nameLower.endsWith('02');
    let namingScoreVal: number;
    if (nameLower.includes('thumb')) namingScoreVal = 1.0;
    else if (nameLower.includes('ball')) namingScoreVal = 0.95;
    else if (
      ['toe', 'dedo_pata', 'dedopata', 'dedo_pate', 'dedopate', 'orteil']
        .some((tok) => nameLower.includes(tok)) &&
      !nameLower.includes('index')
    ) namingScoreVal = 0.9;
    else if (nameLower.includes('index')) namingScoreVal = 0.85;
    else if (isFootNumbered) namingScoreVal = 0.7;
    else namingScoreVal = 0.3;

    let parentScore = 0.5;
    if (bone.parent !== null) {
      const parentName = skel.bone(bone.parent).name;
      parentScore = toeParentScore(parentName);
    }

    const isDirectChildOfFoot = bone.parent === footIdx;
    if (isDirectChildOfFoot) parentScore = Math.min(1.0, parentScore + 0.2);

    let depthFromFoot = 0;
    let current: number | null = boneIdx;
    while (current !== null && current !== footIdx) {
      depthFromFoot++;
      current = current < skel.bones.length ? skel.bone(current).parent : null;
      if (depthFromFoot > 10) break;
    }

    const depthPenalty = Math.max(0.0, 1.0 - (depthFromFoot - 1) * 0.25);
    const topologyScoreVal = parentScore * depthPenalty;
    const dirScore = toeDirectionAlignmentScore(bone.direction, legDir, footDir);

    const breakdown: ScoreBreakdown = {
      topology: topologyScoreVal,
      symmetry: 0.8,
      direction: dirScore,
      lengthRatio: 0.8,
      naming: namingScoreVal,
      depth: depthPenalty,
      total: 0,
    };
    breakdown.total = totalScore(breakdown, WEIGHTS);

    const evidence: Record<string, unknown> = {
      is_direct_child_of_foot: isDirectChildOfFoot,
      depth_from_foot: depthFromFoot,
      has_toe_keyword: namingScoreVal >= 0.85,
      parent_name: bone.parent !== null ? skel.bone(bone.parent).name : null,
    };

    candidates.push(new RoleCandidate({
      boneIdx,
      role,
      score: breakdown,
      evidence,
    }));
  }

  candidates.sort((a, b) => b.total - a.total);
  return candidates;
}

export function splitToeChain(
  skel: Skeleton,
  legChain: number[],
): [number[], number | null] {
  if (legChain.length < 4) return [legChain, null];

  const candidates = findToeCandidates(skel, legChain, 'Toe');
  if (candidates.length === 0) return [legChain, null];

  const best = candidates[0];
  const toeIdx = best.boneIdx;

  if (legChain.includes(toeIdx)) {
    const toePos = legChain.indexOf(toeIdx);
    return [legChain.slice(0, toePos), toeIdx];
  } else {
    const toeBone = skel.bone(toeIdx);
    if (toeBone.parent !== null && legChain.includes(toeBone.parent)) {
      const footPos = legChain.indexOf(toeBone.parent);
      return [legChain.slice(0, footPos + 1), toeIdx];
    }
    return [legChain, toeIdx];
  }
}

export function collectExtraToes(
  skel: Skeleton,
  footIdx: number,
  side: string,
  existingToeIdx: number | null,
): Record<string, number[]> {
  const toes: Record<string, number[]> = {};
  const foot = skel.bone(footIdx);

  const toeCandidates: [number, number][] = [];
  for (const child of foot.children) {
    if (child === existingToeIdx) continue;
    const bone = skel.bone(child);
    if (bone.auxiliary) continue;
    const nameLower = bone.name.toLowerCase();
    if (
      !['toe', 'toes', 'foot_thumb', 'foot_index', 'ball', 'foot', 'dedo_pata', 'dedopata', 'dedo_pate', 'dedopate', 'orteil']
        .some(tok => nameLower.includes(tok))
    ) {
      continue;
    }

    let priority: number;
    if (nameLower.includes('thumb')) priority = 0;
    else if (nameLower.includes('ball')) priority = 1;
    else if (nameLower.includes('index')) priority = 2;
    else if (
      nameLower.includes('toe') ||
      nameLower.includes('dedo_pata') ||
      nameLower.includes('dedopata') ||
      nameLower.includes('dedo_pate') ||
      nameLower.includes('dedopate') ||
      nameLower.includes('orteil')
    ) priority = 3;
    else priority = 4;
    toeCandidates.push([priority, child]);
  }

  if (toeCandidates.length > 0) {
    toeCandidates.sort((a, b) => a[0] - b[0]);
    const bestIdx = toeCandidates[0][1];
    const chainName = `${side}Toe`;
    toes[chainName] = [bestIdx];
  }

  return toes;
}

// ─── Prune inline twist bones (re-exported from helpers for convenience) ─

export { pruneInlineTwistBones } from '../helpers';
